<template>
  <avs-card class="transaction-detail-container">
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template v-slot:title>
      <!-- Desktop: toolbar complète (VERSION ORIGINALE EXACTE) -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-container fluid style="padding: 0;">
          <!-- Filtres -->
          <v-row class="row mb-4">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                @input="onSearchChange"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
                class="search-field"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="230px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="$t('Date de début')"
                    prepend-inner-icon="mdi-calendar-start"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker 
                  v-model="startDate" 
                  @input="applyFilters"
                  color="primary"
                />
              </v-menu>
            </v-col>
            
            <v-col cols="12" md="4">
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    :label="$t('Date de fin')"
                    prepend-inner-icon="mdi-calendar-end"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker 
                  v-model="endDate" 
                  @input="applyFilters"
                  color="primary"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedProduct"
                clearable
                :items="products"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Produits')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-gas-station"
              />
            </v-col>
            
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedCompany"
                clearable
                :items="companies"
                @input="applyFilters"
                item-text="name"
                item-value="pk"
                :label="$t('Entreprises')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-domain"
              />
            </v-col>
            
            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedShop"
                clearable
                @input="applyFilters"
                :items="shops"
                item-text="name"
                item-value="id"
                :label="$t('Stations')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>
            <!-- Actions d'export -->
            <v-col cols="12" md="3" class="d-flex align-center justify-end">
              <v-btn
                color="success"
                outlined
                small
                @click="exportDialog = true"
                class="export-btn mr-2"
              >
                <v-icon small left>mdi-file-excel</v-icon>
                Excel
              </v-btn>
              <v-btn
                color="error"
                outlined
                small
                @click="exportToPdf"
                class="export-btn mr-2"
              >
                <v-icon small left>mdi-file-pdf-box</v-icon>
                PDF
              </v-btn>
              <v-btn
                color="primary"
                outlined
                small
                @click="stationDialog = true"
                class="export-btn"
              >
                <v-icon small left>mdi-calendar-range</v-icon>
                Station
              </v-btn>
            </v-col>
          </v-row>
          <!-- Statistiques / Totaux - Bien séparé -->
          <v-row class="stats-section">
            <v-col cols="12">
              <div class="stats-wrapper">
                <div class="stats-container">
                  <!-- Carte Total Général -->
                  <v-card class="stat-card stat-card-main" elevation="4">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label-mobile">{{ $t('Montant total') }}</div>
                        <div class="stat-value-mobile">{{ formatNumber(totalAmount) }} <span class="stat-unit-mobile">XOF</span></div>
                      </div>
                      <div class="stat-icon-mobile">
                        <v-icon size="40" color="white">mdi-cash-multiple</v-icon>
                      </div>
                    </div>
                    <v-divider class="my-0" style="border-color: rgba(255,255,255,0.3);" />
                    <div class="stat-secondary mt-2">
                      <v-icon small color="white">mdi-gas-station</v-icon>
                      {{ formatNumber(totalQuantity) }} L
                    </div>
                  </v-card>
                  <!-- Cartes par produit -->
                  <v-card
                    v-for="(item, index) in totalByProduct"
                    :key="index"
                    class="stat-card stat-card-product bgTable"
                    elevation="3"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label-mobile">{{ item.name }}</div>
                        <div class="stat-value-mobile">{{ formatNumber(item.total) }} <span class="stat-unit-mobile">XOF</span></div>
                      </div>
                      <div class="stat-icon-mobile">
                        <v-icon size="40">mdi-cash-multiple</v-icon>
                      </div>
                    </div>
                    <div class="stat-secondary-product">
                      <v-icon x-small>mdi-gauge</v-icon>
                      {{ formatNumber(item.total_liters) }} L
                    </div>
                  </v-card>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- ===== MOBILE: DESIGN OPTIMISÉ ===== -->
      <div v-else class="mobile-layout mt-6">
        <!-- Header mobile fixe -->
        <div class="mobile-header-fixed mt-5">
          <div class="mobile-header-content">
            <div class="header-top">
              <div class="header-title-section">
                <v-icon color="primary" large>mdi-receipt-text</v-icon>
                <div class="header-text">
                  <h2 class="header-title">Transactions</h2>
                </div>
              </div>
              <v-btn
                icon
                color="primary"
                @click="showMobileFilters = !showMobileFilters"
              >
                <v-badge
                  :value="hasActiveFilters"
                  color="error"
                  dot
                  overlap
                >
                  <v-icon>{{ showMobileFilters ? 'mdi-close' : 'mdi-filter-variant' }}</v-icon>
                </v-badge>
              </v-btn>
            </div>

            <!-- Stats compactes en chips -->
            <div class="stats-chips-container">
              <div class="stat-chip-main">
                <v-icon small color="white" class="mr-1">mdi-cash</v-icon>
                <span class="stat-chip-value">{{ formatNumber(totalAmount) }}</span>
                <span class="stat-chip-unit">XOF</span>
              </div>
              <div class="stat-chip-secondary">
                <v-icon x-small class="mr-1">mdi-gas-station</v-icon>
                <span>{{ formatNumber(totalQuantity) }} L</span>
              </div>
              <v-menu offset-y v-if="totalByProduct.length > 0">
                <template v-slot:activator="{ on, attrs }">
                  <div class="stat-chip-info" v-bind="attrs" v-on="on">
                    <v-icon x-small class="mr-1">mdi-information</v-icon>
                    <span>Produits</span>
                  </div>
                </template>
                <v-list dense>
                  <v-list-item v-for="(prod, idx) in totalByProduct" :key="idx">
                    <v-list-item-content>
                      <v-list-item-title class="text-caption font-weight-bold">
                        {{ prod.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ formatNumber(prod.total) }} XOF • {{ formatNumber(prod.total_liters) }} L
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- Panneau de filtres mobile (slide down) -->
        <v-expand-transition>
          <div v-show="showMobileFilters" class="mobile-filters-panel">
            <div class="filters-content">
              <!-- Recherche -->
              <v-text-field
                v-model="search"
                @input="onSearchChange"
                clearable
                dense
                outlined
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('Rechercher...')"
                class="mb-3"
              />

              <!-- Dates en row -->
              <v-row dense class="mb-3">
                <v-col cols="6">
                  <v-menu
                    v-model="menuStartDate"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="startDate"
                        label="Début"
                        prepend-inner-icon="mdi-calendar-start"
                        outlined
                        dense
                        hide-details
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker v-model="startDate" @input="applyFilters" />
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-menu
                    v-model="menuEndDate"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="endDate"
                        label="Fin"
                        prepend-inner-icon="mdi-calendar-end"
                        outlined
                        dense
                        hide-details
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker v-model="endDate" @input="applyFilters" />
                  </v-menu>
                </v-col>
              </v-row>

              <!-- Autres filtres -->
              <v-autocomplete
                v-model="selectedProduct"
                clearable
                :items="products"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                label="Produit"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-gas-station"
                class="mb-3"
              />
              <v-autocomplete
                v-model="selectedCompany"
                clearable
                :items="companies"
                @input="applyFilters"
                item-text="name"
                item-value="pk"
                label="Entreprise"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-domain"
                class="mb-3"
              />
              <v-autocomplete
                v-model="selectedShop"
                clearable
                @input="applyFilters"
                :items="shops"
                item-text="name"
                item-value="id"
                label="Station"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-map-marker"
                class="mb-3"
              />

              <!-- Actions export -->
              <div class="filter-actions">
                <v-btn
                  block
                  color="success"
                  outlined
                  small
                  @click="exportDialog = true"
                  class="mb-2"
                >
                  <v-icon small left>mdi-file-excel</v-icon>
                  Exporter Excel
                </v-btn>
                <v-row dense>
                  <v-col cols="6">
                    <v-btn
                      block
                      color="error"
                      outlined
                      small
                      @click="exportToPdf"
                    >
                      <v-icon small left>mdi-file-pdf-box</v-icon>
                      PDF
                    </v-btn>
                  </v-col>
                  <v-col cols="6">
                    <v-btn
                      block
                      color="primary"
                      outlined
                      small
                      @click="stationDialog = true"
                    >
                      <v-icon small left>mdi-calendar-range</v-icon>
                      Station
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </template>

    <!-- ===== CONTENU ===== -->
    <template v-slot:content>
      <!-- Desktop: tableau élégant (VERSION ORIGINALE) -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-data-table
          class="ma-6 bgTable"
          hide-default-footer
          :headers="headers"
          :loading-text="$t('Chargement des données')"
          :no-results-text="$t('Aucune donnée disponible')"
          :loading="isLoading"
          :items="items"
          :items-per-page="-1"
          item-key="id"
          :item-class="rowClass"
          @dblclick:row="
            ($event, { item }) => {
              if (allow.duplicate(item)) {
                handleOpenEditDialog(item);
              }
            }
          "
        >
          <template v-slot:item.company="{ item }">
            <div class="d-flex flex-column">
              <div class="font-weight-bold">
                {{ getComanyName(item) }}
              </div>
              <div class="grey--text text--darken-1 text-caption">
                {{ getDeparmentName(item) }}
              </div>
            </div>
          </template>
          <template v-slot:item.phone_number="{ item }">
            <div class="d-flex flex-column">
              <div class="font-weight-bold">
                {{ getUserOrDriverName(item) }}
              </div>
              <div class="grey--text text--darken-1 text-caption">
                {{ getUserOrVehicleContact(item) }}
              </div>
            </div>
          </template>
          <template v-slot:item.amount="{ item }">
            {{ formatNumber(item.amount) }}
          </template>
          <template v-slot:item.quantity="{ item }">
            {{ formatNumber(item.quantity) }}
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ toDatetime(item.created_at) }}
          </template>
          <template v-slot:item.product="{ item }">
            <div class="d-flex flex-column align-center">
              <v-chip small label color="accent" text-color="white">
                <v-icon left x-small>mdi-gas-station</v-icon>
                {{ item.product || 'N/A' }}
              </v-chip>
              <v-tooltip v-if="isBusiness && item.fraud_details && item.fraud_details.product_auto_corrected" bottom>
                <template #activator="{ on }">
                  <v-chip v-on="on" x-small color="light-green lighten-4" class="mt-1 px-1">
                    <v-icon x-small color="light-green darken-2" left>mdi-auto-fix</v-icon>
                    <span class="light-green--text text--darken-2" style="font-size:10px">Auto-corrigé</span>
                  </v-chip>
                </template>
                <span>Produit corrigé automatiquement par OCR</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <div class="d-flex align-center justify-center">
              <template v-if="isBusiness">
                <template v-if="item.fraud_details && item.fraud_details.ocr_completed">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-icon
                        v-on="on"
                        class="mr-1"
                        :color="item.fraud_details.ocr_amount_match ? 'success' : 'error'"
                      >
                        {{ item.fraud_details.ocr_amount_match ? 'mdi-cash-check' : 'mdi-cash-remove' }}
                      </v-icon>
                    </template>
                    <span>
                      {{ item.fraud_details.ocr_amount_match ? 'Montant OCR correspond' : 'Écart montant OCR' }}
                      <span v-if="item.fraud_details.ocr_closest_amount">
                        ({{ item.fraud_details.ocr_closest_amount }} XOF, écart {{ item.fraud_details.ocr_amount_deviation_pct }}%)
                      </span>
                    </span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-icon
                        v-on="on"
                        :color="item.fraud_details.ocr_product_match ? 'success' : 'warning'"
                      >
                        {{ item.fraud_details.ocr_product_match ? 'mdi-fuel' : 'mdi-alert-circle-outline' }}
                      </v-icon>
                    </template>
                    <span>
                      Produit : {{ item.fraud_details.ocr_product_match ? 'Trouvé sur la pompe' : 'Non détecté' }}
                    </span>
                  </v-tooltip>
                </template>
                <v-tooltip v-else-if="item.image && (!item.fraud_details || !item.fraud_details.ocr_completed)" bottom>
                  <template #activator="{ on }">
                    <v-icon v-on="on" color="grey lighten-1">mdi-text-search</v-icon>
                  </template>
                  <span>OCR en attente</span>
                </v-tooltip>
              </template>
              <component v-else :is="handleStatusComponent(item)" />
            </div>
          </template>
          <template v-slot:item.shop="{ item }">
            <div class="d-flex flex-column">
              <div class="font-weight-bold">
                {{ item.shop }}
              </div>
              <div class="grey--text text--darken-1 text-caption">
                #{{ item.transaction_number }}
              </div>
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center">
              <avs-button
                icon
                color="secondary"
                :iconI="'mdi-eye'"
                label
                fab
                small
                @click.stop="openImageModal(item)"
              />
            </div>
          </template>
        </v-data-table>
        <v-row justify="center" class="my-4" v-if="isFetchingMore && !isLastPage">
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </div>

      <!-- ===== MOBILE: CARTES OPTIMISÉES ===== -->
      <div v-else class="mobile-cards-container p-0">
        <!-- Loading initial -->
        <div v-if="isLoading && !items.length" class="loading-state">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
          />
          <p class="mt-3 grey--text">Chargement...</p>
        </div>

        <!-- Empty state -->
        <div v-else-if="!items.length" class="empty-state">
          <v-icon size="64" color="grey lighten-1">mdi-inbox-remove</v-icon>
          <p class="mt-3 grey--text text--darken-1">Aucune transaction trouvée</p>
          <v-btn
            v-if="hasActiveFilters"
            text
            color="primary"
            small
            @click="clearFilters"
          >
            <v-icon left small>mdi-filter-off</v-icon>
            Réinitialiser les filtres
          </v-btn>
        </div>

        <!-- Liste des cartes -->
        <div v-else class="cards-list">
          <v-card
            v-for="item in items"
            :key="item.id"
            class="transaction-card-mobile"
            @click="openImageModal(item)"
          >
            <!-- En-tête avec avatar et infos principales -->
            <div class="card-header-mobile">
              <div class="company-avatar">
                <span class="avatar-text">{{ getInitials(getComanyName(item)) }}</span>
              </div>
              <div class="header-info">
                <h3 class="company-name-mobile">{{ getComanyName(item) || 'N/A' }}</h3>
                <p class="header-meta">
                  <span class="meta-item">
                    <v-icon x-small color="grey darken-1">mdi-account</v-icon>
                    {{ getUserOrDriverName(item) }}
                  </span>
                </p>
              </div>
              <v-btn
                icon
                small
                color="grey"
                @click.stop="openImageModal(item)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </div>

            <!-- Montant principal en évidence -->
            <div class="amount-highlight">
              <div class="amount-section">
                <span class="amount-label">Montant</span>
                <span class="amount-value">{{ formatNumber(item.amount) }}</span>
                <span class="amount-currency">XOF</span>
              </div>
              <v-divider vertical class="mx-3" />
              <div class="quantity-section">
                <span class="quantity-label">Quantité</span>
                <span class="quantity-value">{{ formatNumber(item.quantity) }}</span>
                <span class="quantity-unit">L</span>
              </div>
            </div>

            <!-- Informations détaillées -->
            <div class="card-details">
              <div class="detail-row">
                <div class="detail-left">
                  <v-icon small color="grey darken-1">mdi-barrel</v-icon>
                  <span class="detail-label">Produit</span>
                </div>
                <v-chip x-small label color="accent" text-color="white" class="detail-chip">
                  {{ item.product || 'N/A' }}
                </v-chip>
              </div>
              
              <div class="detail-row">
                <div class="detail-left">
                  <v-icon small color="grey darken-1">mdi-map-marker</v-icon>
                  <span class="detail-label">Station</span>
                </div>
                <span class="detail-value">{{ item.shop || 'N/A' }}</span>
              </div>
              
              <div class="detail-row">
                <div class="detail-left">
                  <v-icon small color="grey darken-1">mdi-car-back</v-icon>
                  <span class="detail-label">Matricule</span>
                </div>
                <span class="detail-value">{{ getUserOrVehicleContact(item) }}</span>
              </div>
              
              <div class="detail-row">
                <div class="detail-left">
                  <v-icon small color="grey darken-1">mdi-clock-outline</v-icon>
                  <span class="detail-label">Date</span>
                </div>
                <span class="detail-value">{{ toDate(item.created_at) }}</span>
              </div>
            </div>

            <!-- Footer avec transaction number -->
            <div class="card-footer-mobile">
              <span class="transaction-number">#{{ item.transaction_number }}</span>
              <div class="d-flex align-center">
                <template v-if="isBusiness">
                  <template v-if="item.fraud_details && item.fraud_details.ocr_completed">
                    <v-icon
                      small
                      class="mr-1"
                      :color="item.fraud_details.ocr_amount_match ? 'success' : 'error'"
                    >
                      {{ item.fraud_details.ocr_amount_match ? 'mdi-cash-check' : 'mdi-cash-remove' }}
                    </v-icon>
                    <v-icon
                      small
                      :color="item.fraud_details.ocr_product_match ? 'success' : 'warning'"
                    >
                      {{ item.fraud_details.ocr_product_match ? 'mdi-fuel' : 'mdi-alert-circle-outline' }}
                    </v-icon>
                  </template>
                  <v-icon v-else-if="item.image && (!item.fraud_details || !item.fraud_details.ocr_completed)" small color="grey lighten-1">
                    mdi-text-search
                  </v-icon>
                </template>
                <component v-else :is="handleStatusComponent(item)" />
              </div>
            </div>
          </v-card>
        </div>

        <!-- Indicateur de chargement pour pagination -->
        <div v-if="isFetchingMore && !isLastPage" class="loading-more">
          <v-progress-circular
            indeterminate
            color="primary"
            size="32"
          />
          <span class="ml-2 grey--text">Chargement...</span>
        </div>

        <!-- Fin de liste -->
        <div v-if="isLastPage && items.length > 0" class="end-of-list">
          <v-icon color="grey lighten-1">mdi-check-circle</v-icon>
          <span class="ml-2 grey--text">Fin de la liste</span>
        </div>
      </div>

      <!-- ===== DIALOGS (VERSION ORIGINALE) ===== -->
      <v-dialog
        v-model="imageDialog"
        max-width="900px"
        :fullscreen="$vuetify.breakpoint.xs"
      >
        <v-card v-if="selectedItemForImage">
          <v-card-title class="d-flex align-center">
            <v-icon left color="primary">mdi-image-multiple</v-icon>
            Preuves de transaction
            <v-spacer />
            <v-btn icon @click="imageDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-card outlined class="mb-4">
              <v-card-text>
                <v-row dense>
                  <v-col cols="6" sm="3">
                    <div class="text-caption grey--text">N° Transaction</div>
                    <div class="font-weight-bold">{{ selectedItemForImage.transaction_number }}</div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="text-caption grey--text">Montant</div>
                    <div class="font-weight-bold success--text">{{ formatNumber(selectedItemForImage.amount) }} XOF</div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="text-caption grey--text">Quantité</div>
                    <div class="font-weight-bold info--text">{{ formatNumber(selectedItemForImage.quantity) }} L</div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="text-caption grey--text">Date</div>
                    <div class="font-weight-bold">{{ toDatetime(selectedItemForImage.created_at) }}</div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
            <v-row>
              <v-col cols="12" :md="selectedItemForImage.vehicle_image ? 6 : 12">
                <v-card outlined>
                  <v-card-title class="subtitle-2 py-2">
                    <v-icon small left color="primary">mdi-gas-station</v-icon>
                    Photo de la pompe
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="pa-2">
                    <v-img
                      v-if="selectedItemForImage.image"
                      :src="selectedItemForImage.image"
                      max-height="400"
                      contain
                      class="image-preview"
                      @click="openImageFullscreen(selectedItemForImage.image)"
                    >
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="primary" />
                        </v-row>
                      </template>
                    </v-img>
                    <v-alert v-else type="warning" dense text>
                      Image non disponible
                    </v-alert>
                  </v-card-text>
                  <v-divider />
                  <v-card-actions>
                    <v-btn
                      v-if="selectedItemForImage.image"
                      small
                      text
                      color="primary"
                      :href="selectedItemForImage.image"
                      target="_blank"
                    >
                      <v-icon small left>mdi-open-in-new</v-icon>
                      Ouvrir en grand
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
              <v-col cols="12" md="6" v-if="selectedItemForImage.vehicle_image">
                <v-card outlined>
                  <v-card-title class="subtitle-2 py-2">
                    <v-icon small left color="primary">mdi-car</v-icon>
                    Photo du véhicule
                  </v-card-title>
                  <v-divider />
                  <v-card-text class="pa-2">
                    <v-img
                      :src="selectedItemForImage.vehicle_image"
                      max-height="400"
                      contain
                      class="image-preview"
                      @click="openImageFullscreen(selectedItemForImage.vehicle_image)"
                    >
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="primary" />
                        </v-row>
                      </template>
                    </v-img>
                  </v-card-text>
                  <v-divider />
                  <v-card-actions>
                    <v-btn
                      small
                      text
                      color="primary"
                      :href="selectedItemForImage.vehicle_image"
                      target="_blank"
                    >
                      <v-icon small left>mdi-open-in-new</v-icon>
                      Ouvrir en grand
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>

            <!-- Résultats OCR (uniquement pour les sociétés Business) -->
            <template v-if="isBusiness && selectedItemForImage && selectedItemForImage.fraud_details && selectedItemForImage.fraud_details.ocr_completed">
              <v-divider class="my-3" />
              <div class="subtitle-2 mb-2">
                <v-icon small class="mr-1">mdi-text-search</v-icon>
                Analyse OCR de la photo pompe
                <v-chip v-if="selectedItemForImage.fraud_details.ocr_engine_used" x-small class="ml-2" outlined>
                  {{ selectedItemForImage.fraud_details.ocr_engine_used }}
                </v-chip>
              </div>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-alert
                    dense
                    text
                    :type="selectedItemForImage.fraud_details.ocr_amount_match ? 'success' : 'error'"
                  >
                    <div class="font-weight-bold">Montant</div>
                    <div v-if="selectedItemForImage.fraud_details.ocr_closest_amount">
                      OCR : {{ selectedItemForImage.fraud_details.ocr_closest_amount }} XOF
                      <br />
                      Déclaré : {{ selectedItemForImage.amount }} XOF
                      <br />
                      Écart : {{ selectedItemForImage.fraud_details.ocr_amount_deviation_pct }}%
                    </div>
                    <div v-else class="caption">Aucun montant détecté</div>
                  </v-alert>
                </v-col>
                <v-col cols="12" md="6">
                  <v-alert
                    dense
                    text
                    :type="selectedItemForImage.fraud_details.ocr_product_match ? 'success' : 'warning'"
                  >
                    <div class="font-weight-bold">Produit</div>
                    <div>
                      {{ selectedItemForImage.fraud_details.ocr_product_match ? 'Trouvé sur la pompe' : 'Non détecté sur la pompe' }}
                    </div>
                    <div
                      v-if="selectedItemForImage.fraud_details.ocr_product_keywords_found && selectedItemForImage.fraud_details.ocr_product_keywords_found.length"
                      class="caption"
                    >
                      Mots-clés : {{ selectedItemForImage.fraud_details.ocr_product_keywords_found.join(', ') }}
                    </div>
                  </v-alert>
                </v-col>
              </v-row>
            </template>

            <!-- Correction automatique produit par OCR -->
            <template v-if="isBusiness && selectedItemForImage && selectedItemForImage.fraud_details && selectedItemForImage.fraud_details.product_auto_corrected">
              <v-divider class="my-3" />
              <div class="subtitle-2 mb-2 d-flex align-center">
                <v-icon small class="mr-1" color="light-green darken-2">mdi-auto-fix</v-icon>
                <span class="light-green--text text--darken-2">Correction automatique par OCR</span>
              </div>
              <v-alert dense text color="light-green" icon="mdi-swap-horizontal">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <div class="caption grey--text text--darken-1">Produit</div>
                    <div>
                      <span class="grey--text">{{ selectedItemForImage.fraud_details.product_correction && selectedItemForImage.fraud_details.product_correction.old_product_name }}</span>
                      <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                      <span class="font-weight-bold">{{ selectedItemForImage.fraud_details.product_correction && selectedItemForImage.fraud_details.product_correction.new_product_name }}</span>
                    </div>
                  </v-col>
                  <v-col cols="12" md="6" v-if="selectedItemForImage.fraud_details.original_transaction_data">
                    <div class="caption grey--text text--darken-1">Montant</div>
                    <div>
                      <span class="grey--text">{{ selectedItemForImage.fraud_details.original_transaction_data.amount }} XOF</span>
                      <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                      <span class="font-weight-bold">{{ selectedItemForImage.amount }} XOF</span>
                    </div>
                    <div v-if="selectedItemForImage.fraud_details.original_transaction_data.quantity" class="caption mt-1">
                      <span class="grey--text">Quantité : </span>{{ selectedItemForImage.fraud_details.original_transaction_data.quantity }} L
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <div class="caption grey--text mt-1">
                      <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                      Identifié par prix unitaire OCR · Prix unitaire détecté : {{ selectedItemForImage.fraud_details.product_correction && selectedItemForImage.fraud_details.product_correction.resolved_unit_price }} XOF/L
                    </div>
                  </v-col>
                </v-row>
              </v-alert>
            </template>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="imageDialog = false">
              Fermer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Modal station (date unique) — inchangee -->
      <v-dialog
        v-model="stationDialog"
        max-width="400px"
        :fullscreen="$vuetify.breakpoint.xs"
        transition="dialog-bottom-transition"
      >
        <v-card>
          <v-card-title class="headline d-flex align-center">
            <v-icon left color="primary">mdi-calendar-range</v-icon>
            Choisir la date
          </v-card-title>
          <v-card-text>
            <v-menu
              v-model="stationMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="stationDate"
                  label="Date de station"
                  prepend-inner-icon="mdi-calendar"
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="stationDate" @input="stationMenu = false" />
            </v-menu>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="stationDialog = false">{{ $t('commons.cancel') }}</v-btn>
            <v-btn color="primary" @click="exportStation">Exporter</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Modal choix de format d'export Excel sur la plage de dates -->
      <v-dialog
        v-model="exportDialog"
        max-width="600px"
        :fullscreen="$vuetify.breakpoint.xs"
        transition="dialog-bottom-transition"
      >
        <v-card>
          <v-card-title class="headline d-flex align-center">
            <v-icon left color="success">mdi-file-excel</v-icon>
            {{ $t("Choisir le format d'export") }}
          </v-card-title>
          <v-card-subtitle class="pt-1">
            {{ $t("Periode") }} : <strong>{{ startDate }}</strong> &rarr; <strong>{{ endDate }}</strong>
          </v-card-subtitle>
          <v-card-text>
            <v-row dense align="stretch">
              <v-col cols="12" md="6" class="d-flex">
                <v-card
                  outlined
                  class="export-choice-card pa-4 flex-grow-1 d-flex flex-column"
                  hover
                  @click="chooseExport('direct')"
                >
                  <div class="d-flex align-center mb-2">
                    <v-icon color="success" large class="mr-2">mdi-file-excel-outline</v-icon>
                    <span class="text-h6">{{ $t("Export direct") }}</span>
                  </div>
                  <div class="caption grey--text text--darken-2">
                    {{ $t("Export classique sur la plage de dates choisie, sans decalage horaire.") }}
                  </div>
                  <v-btn
                    block
                    color="success"
                    class="mt-auto"
                    :loading="isExportingExcel"
                    :disabled="isExportingExcel || isExportingStation"
                    @click.stop="chooseExport('direct')"
                  >
                    {{ $t("Exporter") }}
                  </v-btn>
                </v-card>
              </v-col>
              <v-col cols="12" md="6" class="d-flex">
                <v-card
                  outlined
                  class="export-choice-card pa-4 flex-grow-1 d-flex flex-column"
                  hover
                  @click="chooseExport('station')"
                >
                  <div class="d-flex align-center mb-2">
                    <v-icon color="primary" large class="mr-2">mdi-gas-station</v-icon>
                    <span class="text-h6">{{ $t("Format station") }}</span>
                  </div>
                  <div class="caption grey--text text--darken-2">
                    {{ $t("Decoupage station : du {start} 08h00 au {end} 07h59 (jour de fin + 1 a 07h59).", { start: startDate, end: endDate }) }}
                  </div>
                  <div class="caption font-weight-bold primary--text mt-1">
                    <v-icon small color="primary">mdi-information</v-icon>
                    {{ $t("Format utilise pour la facturation.") }}
                  </div>
                  <v-btn
                    block
                    color="primary"
                    class="mt-auto"
                    :loading="isExportingStation"
                    :disabled="isExportingExcel || isExportingStation"
                    @click.stop="chooseExport('station')"
                  >
                    {{ $t("Exporter") }}
                  </v-btn>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              :disabled="isExportingExcel || isExportingStation"
              @click="exportDialog = false"
            >
              {{ $t("commons.cancel") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-overlay :value="isExportingPdf" absolute opacity="0.9" z-index="9999">
        <div class="export-overlay-content">
          <v-progress-circular
            indeterminate
            size="64"
            width="6"
            color="error"
          />
          <div class="mt-4 font-weight-bold">Génération du PDF...</div>
        </div>
      </v-overlay>
      <v-overlay :value="isExportingExcel" absolute opacity="0.9" z-index="9999">
        <div class="export-overlay-content">
          <v-progress-circular
            indeterminate
            size="64"
            width="6"
            color="success"
          />
          <div class="mt-4 font-weight-bold">Génération de l'Excel...</div>
        </div>
      </v-overlay>
      <v-overlay :value="isExportingStation" absolute opacity="0.9" z-index="9999">
        <div class="export-overlay-content">
          <v-progress-circular
            indeterminate
            size="64"
            width="6"
            color="primary"
          />
          <div class="mt-4 font-weight-bold">Préparation de l'export station...</div>
        </div>
      </v-overlay>
    </template>
  </avs-card>
</template>

<script>
import { toDatetime, toDate } from "@/utils/datetime";
import { Accepted, Canceled, Pending } from "@/components/generics/status";
import { debounce } from "lodash";
import {
  TransactionDetailService,
  CompanyService,
  ProductDetailService,
  ShopDetailService
} from "@/services";
import { mkUnique } from "@/utils/validator";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "TransactionDetail",
  components: { Accepted, Canceled, Pending },
  
  created() {
    this.fetchFieldTypes();
    this.fetchCompanyTypes();
    this.fetchProductsTypes();
    this.fetchShopTypes();
  },
  
  computed: {
    isBusiness() {
      return this.$store.state.app.company?.company_type === 'Business';
    },
    hasUniqueName: {
      get() {
        const names = this.items
          .filter((e) => e.name !== this.selectedItem?.name)
          .map((e) => e.name);
        return mkUnique(names, "name");
      },
    },
    department() {
      return this.$store.state.app.project;
    },
    hasActiveFilters() {
      return !!(
        this.search ||
        this.selectedProduct ||
        this.selectedCompany ||
        this.selectedShop ||
        this.startDate !== this.formatDateToString(this.getFirstDayOfMonth()) ||
        this.endDate !== this.formatDateToString(this.getLastDayOfMonth())
      );
    }
  },
  
  data() {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    return {
      menu: false,
      page: 1,
      isLastPage: false,
      isFetchingMore: false,
      showMobileFilters: false,
      
      imageDialog: false,
      selectedItemForImage: null,
      
      headers: [
        { text: this.$t("Entreprises"), value: "company", sortable: false, width: 250 },
        { text: this.$t("N° Client"), value: "phone_number", sortable: false, width: 250 },
        { text: this.$t("Coûts"), value: "amount", sortable: false, filterable: false },
        { text: this.$t("Produit"), value: "product", align: "center", sortable: true, filterable: false },
        { text: this.$t("Litre"), value: "quantity", align: "center", sortable: false, filterable: false },
        { text: this.$t("Servi le"), value: "created_at", sortable: true, filterable: false },
        { text: this.$t("Station"), value: "shop", sortable: true, filterable: false, width: 200  },
        { text: this.$t("Statut"), value: "status", align: "center", sortable: false, filterable: false },
        { text: this.$t("Preuve"), value: "actions", align: "center", sortable: false, filterable: false },
      ],
      
      isEditable: true,
      items: [],
      search: this.$route.query.search || "",
      companies: [],
      shops: [],
      products: [],
      
      isExportingPdf: false,
      isExportingExcel: false,
      isExportingStation: false,
      
      totalByProduct: [],
      startDate: this.formatDateToString(firstDayOfMonth),
      endDate: this.formatDateToString(lastDayOfMonth),
      selectedProduct: null,
      selectedCompany: null,
      selectedShop: null,
      menuStartDate: false,
      menuEndDate: false,
      totalAmount: 0,
      totalQuantity: 0,
      isLoading: false,
      getTransactionDebounced: null,
      deleteDialogIsOpen: false,
      editDialogIsOpen: false,
      editDialogContent: null,
      stationDialog: null,
      exportDialog: false,
      selectedItem: undefined,
      stationMenu: false,
      stationDate: this.formatDateToString(new Date()),
    };
  },
  
  methods: {
    toDatetime,
    toDate,
    
    handleStatusComponent(item) {
      const s = (item.status || '').toLowerCase();
      if (s === 'pending') return 'Pending';
      if (s === 'canceled' || s === 'rejected') return 'Canceled';
      return 'Accepted';
    },
    rowClass(row) {
      const item = row && row.item ? row.item : row;
      if (!this.isBusiness) return [];
      const fd = item?.fraud_details;
      if (fd && fd.product_auto_corrected) {
        return ["ocr-auto-corrected"];
      }
      if (fd && fd.ocr_completed && (!fd.ocr_amount_match || !fd.ocr_product_match)) {
        return ["ocr-mismatch"];
      }
      return [];
    },
    normalizedSearch() {
      return this.search == null ? "" : this.search;
    },
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    
    openImageModal(item) {
      this.selectedItemForImage = item;
      this.imageDialog = true;
    },
    
    openImageFullscreen(imageUrl) {
      window.open(imageUrl, '_blank');
    },
    
    statusLabel(it) {
      const s = (it.status || '').toLowerCase();
      if (s === 'pending') return this.$t('En attente');
      if (s === 'accepted' || s === 'done' || s === 'effectué') return this.$t('Effectué');
      if (s === 'canceled' || s === 'rejected') return this.$t('Annulé');
      return s || this.$t('Effectué');
    },
    
    statusColor(it) {
      const s = (it.status || '').toLowerCase();
      if (s === 'pending') return 'warning';
      if (s === 'accepted' || s === 'done' || s === 'effectué') return 'success';
      if (s === 'canceled' || s === 'rejected') return 'error';
      return 'success';
    },
    
    statusIcon(it) {
      const s = (it.status || '').toLowerCase();
      if (s === 'pending') return 'mdi-clock-outline';
      if (s === 'accepted' || s === 'done' || s === 'effectué') return 'mdi-check-circle';
      if (s === 'canceled' || s === 'rejected') return 'mdi-close-circle';
      return 'mdi-check-circle';
    },
    
    async loadMoreTransactions() {
      if (this.isLoading || this.isFetchingMore || this.isLastPage) return;
      
      this.isFetchingMore = true;
      const filters = {
        search: this.search,
        startDate: this.startDate,
        endDate: this.endDate,
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        department: this.department?.id || null,
        page: this.page,
      };
      
      try {
        const response = await TransactionDetailService.getAll(this, filters);
        const results = response.results.results;
        
        if (!results.length) {
          this.isLastPage = true;
        } else {
          this.items.push(...results);
          this.page += 1;
          this.totalAmount = response.results.total_amount;
          this.totalQuantity = response.results.total_quantity || 0;
          this.totalByProduct = response.results.total_by_product || [];
        }
      } catch (error) {
        if (error?.response?.status === 404) this.isLastPage = true;
        console.error("Erreur chargement transactions", error);
      } finally {
        this.isFetchingMore = false;
        this.$nextTick(() => { if (!this.isLastPage) this.handleScroll() });
      }
    },

    exportToPdf() {
      this.isExportingPdf = true;
      const filters = {
        search: this.search,
        startDate: this.startDate,
        endDate: this.endDate,
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        department: this.department?.id || null,
        no_pagination: true
      };
      
      TransactionDetailService.getAll(this, filters)
        .then((response) => {
          const items = response.results?.results || response.results || [];
          if (items.length === 0) {
            this.send(this.$t("messages.warning.no_data_to_export"), "warning");
            return;
          }
          
          const doc = new jsPDF("landscape");
          const title = `Liste des transactions - ${new Date().toLocaleDateString()}`;
          doc.setFontSize(14);
          doc.text(title, 14, 15);
          
          const exportData = items.map((item, index) => [
            index + 1,
            item.transaction_number || "N/A",
            this.getComanyName(item),
            item.vehicle?.registration_number || item.user?.phone_number || "N/A",
            this.getUserOrDriverName(item),
            this.formatNumber(item.amount),
            item.product || "N/A",
            this.formatNumber(item.quantity),
            this.toDatetime(item.created_at),
            item.shop || "N/A",
            item.status || "Effectué",
          ]);
          
          autoTable(doc, {
            head: [[
              "#", "N° Transaction", "Société", "Client / Matricule", "Nom & Prénom",
              "Montant (XOF)", "Produit", "Quantité (L)", "Date", "Station", "Statut"
            ]],
            body: exportData,
            startY: 20,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [0, 92, 185] }
          });
          
          doc.save(`transactions_${new Date().toISOString().slice(0, 10)}.pdf`);
          this.send(this.$t("Téléchargé avec succès"), "success");
        })
        .catch((err) => {
          console.error("Erreur export PDF :", err);
          this.send(this.$t("messages.error.export_failed"), "error");
        })
        .finally(() => {
          this.isExportingPdf = false;
        });
    },
    
    handleScroll() {
      const scrollPosition = window.innerHeight + window.scrollY;
      const bottom = document.body.offsetHeight - 300;
      if (scrollPosition >= bottom) this.loadMoreTransactions();
    },
    
    onSearchChange() {
      if (this.search == null) this.search = "";
      this.getTransactionDebounced();
    },
    
    formatDateToString(date) {
      // IMPORTANT : on prend les composantes LOCALES (getFullYear/getMonth/getDate),
      // jamais toISOString() qui passe en UTC et decale d un jour selon le fuseau.
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    
    getFirstDayOfMonth() {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), 1);
    },
    
    getLastDayOfMonth() {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth() + 1, 0);
    },
    
    clearFilters() {
      this.search = "";
      this.selectedProduct = null;
      this.selectedCompany = null;
      this.selectedShop = null;
      this.startDate = this.formatDateToString(this.getFirstDayOfMonth());
      this.endDate = this.formatDateToString(this.getLastDayOfMonth());
      this.applyFilters();
    },
    
    fetchFieldTypes() {
      this.isLoading = true;
      const filters = {
        search: this.search,
        startDate: this.startDate,
        endDate: this.endDate,
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        department: this.department?.id || null,
        page: this.page,
      };
      
      TransactionDetailService.getAll(this, filters)
        .then((response) => {
          this.items = [...response.results.results];
          this.page = 2;
          this.isLastPage = response.results.results.length === 0;
          this.totalAmount = response.results.total_amount;
          this.totalQuantity = response.results.total_quantity || 0;
          this.totalByProduct = response.results.total_by_product || [];
        })
        .catch((err) => {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error",
            });
          }
        })
        .finally(() => {
          this.isLoading = false;
          this.$nextTick(() => { if (!this.isLastPage) this.handleScroll() });
        });
    },

    fetchCompanyTypes() {
      CompanyService.getAllCompanies().then(r => {
        this.companies = r;
      });
    },
    
    fetchProductsTypes() {
      ProductDetailService.getprincipalAll().then(r => {
        this.products = r.results;
      });
    },
    
    fetchShopTypes() {
      ShopDetailService.getprincipalAll(this).then(r => {
        this.shops = r;
      });
    },
    
    exportToExcel() {
      this.isExportingExcel = true;
      const filters = {
        search: this.search,
        startDate: this.startDate,
        endDate: this.endDate,
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
      };
      // Nettoyer les valeurs null/vides
      Object.keys(filters).forEach(k => {
        if (!filters[k] || filters[k] === "null") delete filters[k];
      });

      TransactionDetailService.exportExcel(filters)
        .then(blob => {
          saveAs(blob, `transactions_${new Date().toISOString().slice(0, 10)}.xlsx`);
          this.send(this.$t("Telecharge avec succes"), "success");
        })
        .catch(err => {
          console.error("Erreur export Excel :", err);
          this.send(this.$t("messages.error.export_failed"), "error");
        })
        .finally(() => {
          this.isExportingExcel = false;
        });
    },
    
    formatNumber(value) {
      if (!value) return "0";
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    
    getUserOrVehicleContact(item) {
      if (item.vehicle) return item.vehicle.registration_number
      if (item.donation_used && item.donation_used.first_name) return `${item.donation_used.phone_number}`;
      return item.vehicle ? item.vehicle.registration_number : (item.user?.phone_number || "N/A");
    },
    
    getVehicleBL(item) {
      if (item.vehicle && item.vehicle.voucher_used) return `${item.vehicle.voucher_used.bl_number}`;
      return "N/A";
    },
    
    getVehicleDestionation(item) {
      if (item.vehicle && item.vehicle.voucher_used) return `${item.vehicle.voucher_used.destination}`;
      return "N/A";
    },
    
    getComanyName(item) {
      if (item.vehicle) return item.vehicle.company;
      if (item.donation_used) return item.donation_used.company_name;
      if (item.company_name) return item.company_name;
      if (item.user.phone_number == "+2250000000009") {
        return item.created_by.company.name;
      }
      return "";
    },
    
    getDeparmentName(item) {
      if (item.vehicle) return item.vehicle.department;
      if (item.donation_used) return "Don";
      if (item.user.phone_number == "+2250000000009") {
        return "Don";
      }
      return "-";
    },
    
    getComanyAvalanche(item) {
      if (item.vehicle) return item.vehicle.company_avalanche_id;
      if (item.donation_used) return item.donation_used.company_avalanche_id;
      if (item.company_avalanche_id) return item.company_avalanche_id;
      if (item.user.phone_number == "+2250000000009") {
        return item.created_by.company.company_avalanche_id;
      }
      return "";
    },
    
    getUserOrDriverName(item) {
      if (item.vehicle && item.vehicle.driver_name) return `${item.vehicle.driver_name}`;
      if (item.donation_used && item.donation_used.first_name) return `${item.donation_used.first_name} ${item.donation_used.last_name}`;
      if (item.user && item.user.first_name && item.user.last_name) {
        return `${item.user.first_name} ${item.user.last_name}`;
      }
      return "N/A";
    },
    
    openItemUrl(item) {
      window.open(item.image, "_blank");
    },
    
    send(msg, color) {
      this.$store.commit("app/message", { msg, color });
    },
    
    applyFilters() {
      this.page = 1;
      this.isLastPage = false;
      this.items = [];
      this.fetchFieldTypes();
      if (this.$vuetify.breakpoint.smAndDown) {
        this.showMobileFilters = false;
      }
    },
    
    chooseExport(type) {
      if (this.isExportingExcel || this.isExportingStation) return;
      if (type === "direct") {
        this.exportDialog = false;
        this.exportToExcel();
      } else if (type === "station") {
        // On NE ferme PAS le modal au debut : le spinner reste visible sur
        // le bouton "Exporter" pendant l'envoi. Fermeture sur succes seulement.
        this.exportStationRange();
      }
    },

    // Export format station sur la plage de dates courante :
    // start = jour de debut a 08h00 local, end = lendemain du jour de fin
    // a 07h59:59 local (un "jour station" va de 8h a 7h59 le lendemain).
    // Utilise pour la facturation.
    exportStationRange() {
      this.isExportingStation = true;

      const start = new Date(this.startDate);
      start.setHours(8, 0, 0, 0);
      const end = new Date(this.endDate);
      end.setDate(end.getDate() + 1);
      end.setHours(7, 59, 59, 999);

      const filters = {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        search: this.search,
        exportDate: `${this.startDate} - ${this.endDate}`,
      };
      Object.keys(filters).forEach(k => {
        if (!filters[k] || filters[k] === "null") delete filters[k];
      });

      TransactionDetailService.exportStation(filters)
        .then(blob => {
          saveAs(blob, `station_export_${this.startDate}_${this.endDate}.xlsx`);
          this.send(this.$t("Telecharge avec succes"), "success");
          this.exportDialog = false;
        })
        .catch(err => {
          this.send(this.$t("messages.error.export_failed"), "error");
          console.error(err);
        })
        .finally(() => {
          this.isExportingStation = false;
        });
    },

    exportStation() {
      this.stationDialog = false;
      this.isExportingStation = true;

      const sel = new Date(this.stationDate);
      const start = new Date(sel);
      start.setHours(8, 0, 0, 0);
      const end = new Date(sel);
      end.setDate(end.getDate() + 1);
      end.setHours(8, 0, 0, 0);

      const filters = {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        search: this.search,
        exportDate: this.formatDateToString(sel),
      };
      Object.keys(filters).forEach(k => {
        if (!filters[k] || filters[k] === "null") delete filters[k];
      });

      TransactionDetailService.exportStation(filters)
        .then(blob => {
          saveAs(blob, `station_export_${this.stationDate}.xlsx`);
          this.send(this.$t("Telecharge avec succes"), "success");
        })
        .catch(err => {
          this.send(this.$t("messages.error.export_failed"), "error");
          console.error(err);
        })
        .finally(() => {
          this.isExportingStation = false;
        });
    }
  },
  
  mounted() {
    this.getTransactionDebounced = debounce(() => {
      this.page = 1;
      this.items = [];
      this.isLastPage = false;
      this.fetchFieldTypes();
    }, 500);
    
    this.loadMoreTransactions();
    window.addEventListener("scroll", this.handleScroll);
  },
  
  watch: {
    "$route.query.search"(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.search = newVal || "";
      this.page = 1;
      this.items = [];
      this.isLastPage = false;
      this.fetchFieldTypes();
    },
  },
  
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
};
</script>

<style scoped>
/* ===== STYLES DESKTOP (ORIGINAUX) ===== */
.transaction-detail-container {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
}

.page-header {
  padding: 16px 0;
  border-bottom: 2px solid #e0e0e0;
}

.filters-row {
  gap: 8px;
}

.search-field {
  border-radius: 12px;
}

.export-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
}

.stats-section {
  margin-top: 10px;
  margin-bottom: 0px;
  padding: 5px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border-radius: 16px;
}

.stats-wrapper {
  padding: 0 5px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

.stat-card-main {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  border-radius: 20px !important;
  padding-left: 24px;
  padding-right: 24px;
  padding-top: 0px;
  padding-bottom: 0px;
  color: white;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  position: relative;
  overflow: hidden;
}

.stat-card-main::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  pointer-events: none;
}

.stat-card-main:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 40px rgba(25, 118, 210, 0.4) !important;
}

.stat-label-mobile {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
  margin-bottom: 8px;
}

.stat-value-mobile {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
}

.stat-unit-mobile {
  font-size: 15px;
  font-weight: 600;
}

.stat-icon-mobile {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.stat-secondary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.stat-card-product {
  border-radius: 16px !important;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 0px;
  padding-bottom: 0px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #e3f2fd;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
}

.stat-card-product:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important;
  border-color: #90caf9;
}

.stat-secondary-product {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}

.image-preview {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease;
}

.image-preview:hover {
  transform: scale(1.02);
}

.export-overlay-content {
  text-align: center;
  padding: 32px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  color: #212121;
}

/* ===== MOBILE LAYOUT ===== */
.mobile-layout {
  width: 100%;
  background: white;
}

.mobile-header-fixed {
  position: sticky;
  top: 0;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mobile-header-content {
  padding: 16px;
}

.header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.header-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-text {
  flex: 1;
  min-width: 0;
}

.header-title {
  font-size: 20px;
  font-weight: 800;
  margin: 0;
  color: #212121;
}

.stats-chips-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.stat-chip-main {
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.stat-chip-value {
  font-size: 18px;
  font-weight: 800;
}

.stat-chip-unit {
  font-size: 11px;
  opacity: 0.9;
}

.stat-chip-secondary,
.stat-chip-info {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #e3f2fd;
  color: #1976d2;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
}

.stat-chip-info {
  cursor: pointer;
  transition: all 0.2s ease;
}

.stat-chip-info:active {
  transform: scale(0.95);
  background: #bbdefb;
}

.mobile-filters-panel {
  background: white;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.filters-content {
  padding: 16px;
}

.filter-actions {
  margin-top: 8px;
}

.mobile-cards-container {
  background: #ccd5df;
  min-height: calc(100vh - 200px);
  padding-top: 12px;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.cards-list {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.transaction-card-mobile {
  background: white !important;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  transition: all 0.2s ease;
  cursor: pointer;
}

.transaction-card-mobile:active {
  transform: scale(0.98);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
}

.card-header-mobile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-bottom: 1px solid #e0e0e0;
}

.company-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.avatar-text {
  color: white;
  font-size: 16px;
  font-weight: 800;
}

.header-info {
  flex: 1;
  min-width: 0;
}

.company-name-mobile {
  font-size: 15px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-meta {
  margin: 0;
  font-size: 12px;
  color: #757575;
  display: flex;
  align-items: center;
  gap: 4px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.amount-highlight {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 16px;
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8f2 100%);
  border-bottom: 2px solid #a5d6a7;
}

.amount-section,
.quantity-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.amount-label,
.quantity-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #2e7d32;
  font-weight: 700;
  margin-bottom: 4px;
}

.amount-value {
  font-size: 24px;
  font-weight: 800;
  color: #1b5e20;
  line-height: 1.1;
}

.amount-currency,
.quantity-unit {
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
  opacity: 0.8;
  margin-left: 4px;
}

.quantity-value {
  font-size: 20px;
  font-weight: 800;
  color: #1b5e20;
  line-height: 1.1;
}

.card-details {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 32px;
}

.detail-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-width: 100px;
}

.detail-label {
  font-size: 12px;
  color: #757575;
  font-weight: 600;
}

.detail-value {
  font-size: 13px;
  font-weight: 600;
  color: #212121;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 55%;
}

.detail-chip {
  flex-shrink: 0;
}

.card-footer-mobile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fafafa;
  border-top: 1px solid #e0e0e0;
}

.transaction-number {
  font-size: 11px;
  font-weight: 700;
  color: #757575;
}

.status-chip-mobile {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
}

.loading-more,
.end-of-list {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #757575;
  font-size: 13px;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobile-filters-panel {
  animation: slideIn 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.transaction-card-mobile {
  animation: fadeInUp 0.3s ease-out;
}

@media (max-width: 960px) {
  .stats-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .stat-value-mobile {
    font-size: 28px;
  }
  
  .amount-value {
    font-size: 20px;
  }
  
  .quantity-value {
    font-size: 18px;
  }
}

@media (max-width: 360px) {
  .header-title {
    font-size: 18px;
  }
  
  .stat-chip-value {
    font-size: 16px;
  }
  
  .company-name-mobile {
    font-size: 14px;
  }
  
  .amount-value {
    font-size: 18px;
  }
}

/* ===== Highlight OCR mismatch ===== */
::v-deep .v-data-table__wrapper tbody tr.ocr-mismatch > td {
  background-color: #fffde7 !important;
}

::v-deep .v-data-table__wrapper tbody tr.ocr-mismatch > td:first-child {
  box-shadow: inset 4px 0 0 0 #ffe082;
}

/* ===== Highlight OCR auto-corrected ===== */
::v-deep .v-data-table__wrapper tbody tr.ocr-auto-corrected > td {
  background-color: #f1f8e9 !important;
}

::v-deep .v-data-table__wrapper tbody tr.ocr-auto-corrected > td:first-child {
  box-shadow: inset 4px 0 0 0 #7cb342;
}
</style>